import React from 'react'

import { Color, Display, FlexDirection } from '@gousto-internal/citrus-react'

import { RecipeBanner } from './RecipeBanner'
import { RecipeTileBody } from './RecipeTileBody'
import { RecipeTileCustomisationDropdown } from './RecipeTileCustomisationDropdown'
import { RecipeTileImageSection } from './RecipeTileImageSection'
import { Card, RecipeDetailsOverlayButton } from './RecipeTile.styles'
import type { RecipeTileProps } from './RecipeTile.types'
import { getRecipeTileAriaLabel } from './RecipeTile.utils'

export const RecipeTile = ({
  recipe,
  onClick,
  dataTestingId = recipe.id,
  lazy = true,
  insideOverflow = false,
  isInBasket,
  canAddRecipes,
  reachedLimit,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  customisationData,
  onCustomiseOpen,
  onCustomiseClose,
}: RecipeTileProps) => {
  const isOutOfStock = !recipe.isAvailable && !isInBasket
  const isCustomisationOpen = Boolean(customisationData?.hasOpenedCustomisations)
  const handleDetailsClick = () => onClick(recipe.id)
  const handleCustomiseClick = onCustomiseOpen
    ? () => {
        if (isCustomisationOpen) {
          onCustomiseClose?.()
          return
        }

        onCustomiseOpen(recipe.id)
      }
    : undefined
  const detailsAriaLabel = getRecipeTileAriaLabel(recipe, { isOutOfStock })

  return (
    <Card
      data-testid="recipe-tile"
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      data-testing-id={dataTestingId}
      data-customisations-loading={customisationData?.isLoadingCustomisations}
      data-customisations-opened={customisationData?.hasOpenedCustomisations}
      data-customisations-error={customisationData?.hasCustomisationError}
      bg={Color.White}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
    >
      <RecipeDetailsOverlayButton
        data-testid="recipe-tile-details-button"
        type="button"
        aria-label={detailsAriaLabel}
        onClick={handleDetailsClick}
      />
      <RecipeTileImageSection
        recipe={recipe}
        isOutOfStock={isOutOfStock}
        lazy={lazy}
        insideOverflow={insideOverflow}
      />
      <RecipeTileBody
        recipe={recipe}
        isOutOfStock={isOutOfStock}
        isInBasket={isInBasket}
        canAddRecipes={canAddRecipes}
        reachedLimit={reachedLimit}
        onAddClick={onAddClick}
        onRemoveClick={onRemoveClick}
        onBlockedAddAttempt={onBlockedAddAttempt}
        isCustomisationOpen={isCustomisationOpen}
        onCustomiseClick={handleCustomiseClick}
      />
      <RecipeBanner recipe={recipe} isOutOfStock={isOutOfStock} />
      {isCustomisationOpen && (
        <RecipeTileCustomisationDropdown
          customisationData={customisationData}
          onClose={onCustomiseClose}
        />
      )}
    </Card>
  )
}
